import type { ChatItem } from '../../../contracts/harness.types';

import { deriveTurnLifecycle } from './turnLifecycle';

/** A completed (not latest) turn earns an action row on the same terms the
 *  latest does once settled: it ended on a reply or on a recovered failure. */
export const completedTurnHasFeedbackTarget = (turnItems: ChatItem[]): boolean => {
  const { latestTurnHasReply, lastItem } = deriveTurnLifecycle(turnItems);
  return latestTurnHasReply || lastItem?.kind === 'turn_failed';
};

/**
 * Feedback `message_id` for a completed turn: the transcript position of its
 * last item, i.e. what `String(items.length - 1)` was when it was the latest
 * turn — so a reply rated while latest keeps the same id once it is earlier.
 * Returns null when neither the next turn's opener nor the turn's own last item
 * is in the persisted transcript yet (an optimistic echo).
 */
export const resolveCompletedTurnMessageId = (
  transcriptIndexById: ReadonlyMap<string, number>,
  turnItems: readonly ChatItem[],
  nextTurnFirstItem: ChatItem | undefined
): string | null => {
  const nextStart = nextTurnFirstItem
    ? transcriptIndexById.get(nextTurnFirstItem.id)
    : undefined;
  if (nextStart !== undefined && nextStart > 0) return String(nextStart - 1);
  const lastId = turnItems.at(-1)?.id;
  const ownLast = lastId === undefined ? undefined : transcriptIndexById.get(lastId);
  return ownLast === undefined ? null : String(ownLast);
};
